import styled, { css, keyframes } from 'styled-components';

export const Container = styled.div`
  padding: 1rem 2rem 3rem 2rem;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 1rem 1rem 2rem 1rem;
  }
`;

export const FilterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1.5rem;
  flex-shrink: 0;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

export const SearchContainer = styled.div`
  flex: 1;
  max-width: 500px;
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9em;
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #0C5C75;
    box-shadow: 0 0 0 2px rgba(12, 92, 117, 0.1);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

export const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
`;

export const DeviceCount = styled.div`
  color: #6c757d;
  font-size: 0.9em;
  white-space: nowrap;
`;

export const FilterActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  min-width: 220px;

  @media (max-width: 768px) {
    width: 100%;
    align-items: stretch;
  }
`;

export const DevicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.2rem;
  flex: 1;
  align-content: start;
  width: calc(100% - 1rem);
  margin-bottom: 1rem;

  @media (min-width: 1400px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 1.5rem;
    width: calc(100% - 1.5rem);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 0.5rem;
    width: calc(100% - 0.5rem);
  }
`;

export const DeviceCard = styled.div<{ $batteryColor: string; $isOnline: boolean; $isDisabled?: boolean }>`
  background: ${props => (props.$isDisabled ? '#f1f3f5' : 'white')};
  border-radius: 12px;
  padding: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => (props.$isDisabled ? '#ced4da' : props.$batteryColor)};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  opacity: ${props => (props.$isDisabled ? 0.7 : 1)};

  &:hover {
    transform: ${props => (props.$isDisabled ? 'none' : 'translateY(-2px)')};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

export const StatusMissingBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #dc3545;
  background: #fff5f5;
  box-shadow: 0 0 6px rgba(220, 53, 69, 0.25);
  pointer-events: auto;

  svg {
    flex-shrink: 0;
  }
`;

export const DeviceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  min-width: 0;
`;

export const DeviceName = styled.h3`
  margin: 0;
  font-size: 1.1em;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.3;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DeviceClient = styled.p`
  margin: 0 0 1rem 0;
  color: #6c757d;
  font-size: 0.9em;
`;

export const DeviceMetrics = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const MetricItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9em;
  color: #495057;
  font-weight: 500;
`;

export const LastSeen = styled.div`
  font-size: 0.8em;
  color: #6c757d;
  font-style: italic;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const RefreshButton = styled.button<{ $isLoading: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  background: #0C5C75;
  color: #fff;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.65rem 1.1rem;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background: #0a4f63;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  svg {
    animation: ${props => (props.$isLoading ? css`${spin} 1s linear infinite` : 'none')};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #6c757d;
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e3f2fd;
  border-top: 4px solid #0C5C75;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 1rem;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: #6c757d;
  flex: 1;

  h3 {
    margin: 1rem 0 0.5rem 0;
    color: #495057;
  }

  p {
    margin: 0;
    font-size: 0.9em;
  }
`;

export const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding-bottom: 2rem;
  padding-right: 1rem;
  
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;